'use client';

import { useEffect, useRef, useState } from 'react';
import { ensureSession } from 'services/session/client';
import { useAuthSessionStore } from 'stores/auth';
import { isUserAuthenticated, logger, parseJwtToken, withTimeout } from 'utils';
import { SESSION_JWT_STATUS, SESSION_REQUEST_TIMEOUT_MS } from './constants';
import type { SessionJwt } from './types';

const LOADING_STATE: SessionJwt = {
  status: SESSION_JWT_STATUS.LOADING,
  jwt: null,
  isAuthenticated: false,
  uuid: null,
  version: 0,
  afterLogin: false,
};

function deriveAuth(jwt: string): Pick<SessionJwt, 'isAuthenticated' | 'uuid'> {
  const token = parseJwtToken(jwt);

  return { isAuthenticated: isUserAuthenticated(token), uuid: token?.uuid ?? null };
}

/**
 * Creates or restores the browser session and exposes its JWT with the auth state derived from it.
 *
 * Waits at most SESSION_REQUEST_TIMEOUT_MS: after that the state is ready as anonymous (jwt null), so a hung
 * request never holds back the page. A slow session usually still arrives: the hook keeps waiting and upgrades
 * the state then, so a restored customer ends up authenticated without a reload.
 *
 * A login in the auth dialog (useAuthSessionStore().loginCount increases) goes back to loading and evaluates
 * again, because router.refresh() does not reach this client state.
 */
export function useSessionJwt(): SessionJwt {
  const [state, setState] = useState<SessionJwt>(LOADING_STATE);
  const loginCount = useAuthSessionStore(store => store.loginCount);
  // A count already present at mount is covered by the mount run.
  const loginCountAtMountRef = useRef(loginCount);
  const isFirstRunRef = useRef(true);

  useEffect(() => {
    // Each run owns its writes: the cleanup of the previous run silences it, so a slow older run cannot overwrite
    // a newer result.
    let cancelled = false;
    const afterLogin = loginCount !== loginCountAtMountRef.current;

    // Not on the first run: the initial state is already loading.
    if (!isFirstRunRef.current) setState(previous => ({ ...LOADING_STATE, version: previous.version, afterLogin }));

    isFirstRunRef.current = false;

    async function evaluate() {
      const request = ensureSession();
      let jwt: string | null;

      try {
        jwt = await withTimeout(request, SESSION_REQUEST_TIMEOUT_MS);
      } catch (error) {
        logger.warn({ error }, '[useSessionJwt] Could not create a session in time');

        if (!cancelled) {
          setState(previous => ({
            ...LOADING_STATE,
            status: SESSION_JWT_STATUS.READY,
            version: previous.version,
            afterLogin,
          }));
        }

        // A real failure already rejected above, so it just resolves to null here.
        jwt = await request.catch(() => null);
      }

      if (!jwt || cancelled) return;

      const auth = deriveAuth(jwt);

      setState(previous => ({
        status: SESSION_JWT_STATUS.READY,
        jwt,
        ...auth,
        version: previous.version + 1,
        afterLogin,
      }));
    }

    void evaluate();

    return () => {
      cancelled = true;
    };
  }, [loginCount]);

  return state;
}
